/**
 * MTA Memory 模块 - 知识图谱记忆系统
 * 
 * 动态代理 @modelcontextprotocol/server-memory 的能力
 * 保持依赖更新的同时，提供 MTA 特有的增强功能
 * 
 * @version 2.0.0
 * @since 3.2.0
 */

import path from 'path';

// ================== 类型定义 ==================

export interface Entity {
  name: string;
  entityType: string;
  observations: string[];
}

export interface Relation {
  from: string;
  to: string;
  relationType: string;
}

export interface KnowledgeGraph {
  entities: Entity[];
  relations: Relation[];
}

// ================== 动态导入上游模块 ==================

let KnowledgeGraphManagerClass: any = null;

/**
 * 动态加载 KnowledgeGraphManager 类
 * 这样可以保持依赖更新，同时避免启动时的硬依赖
 */
async function loadKnowledgeGraphManager() {
  if (!KnowledgeGraphManagerClass) {
    try {
      // 动态导入 npm 包
      // @ts-ignore - 该包的类型定义在运行时动态加载
      const module = await import('@modelcontextprotocol/server-memory');
      KnowledgeGraphManagerClass = module.KnowledgeGraphManager;
    } catch (error) {
      console.error('无法加载 @modelcontextprotocol/server-memory:', error);
      throw new Error('知识图谱模块加载失败，请确保已安装 @modelcontextprotocol/server-memory');
    }
  }
  return KnowledgeGraphManagerClass;
}

// ================== 单例管理器 ==================

let memoryManager: any = null;

/**
 * 获取知识图谱管理器单例
 * 使用 MTA 自定义的存储路径
 */
async function getMemoryManager() {
  if (!memoryManager) {
    const Manager = await loadKnowledgeGraphManager();
    // 使用用户主目录下的 .mta 目录存储记忆
    const homeDir = process.env.HOME || process.env.USERPROFILE || '.';
    const memoryPath = path.join(homeDir, '.mta', 'memory.jsonl');
    memoryManager = new Manager(memoryPath);
  }
  return memoryManager;
}

// ================== MCP 工具处理函数 ==================

/**
 * 创建实体
 */
export async function createEntities(args: { entities: Entity[] }): Promise<any> {
  const manager = await getMemoryManager();
  const result = await manager.createEntities(args.entities);
  return {
    content: [{
      type: 'text',
      text: JSON.stringify({
        success: true,
        created: result.length,
        entities: result
      }, null, 2)
    }]
  };
}

/**
 * 创建关系
 */
export async function createRelations(args: { relations: Relation[] }): Promise<any> {
  const manager = await getMemoryManager();
  const result = await manager.createRelations(args.relations);
  return {
    content: [{
      type: 'text',
      text: JSON.stringify({
        success: true,
        created: result.length,
        relations: result
      }, null, 2)
    }]
  };
}

/**
 * 添加观察
 */
export async function addObservations(args: { observations: { entityName: string; contents: string[] }[] }): Promise<any> {
  const manager = await getMemoryManager();
  const result = await manager.addObservations(args.observations);
  return {
    content: [{
      type: 'text',
      text: JSON.stringify({
        success: true,
        results: result
      }, null, 2)
    }]
  };
}

/**
 * 删除实体
 */
export async function deleteEntities(args: { entityNames: string[] }): Promise<any> {
  const manager = await getMemoryManager();
  await manager.deleteEntities(args.entityNames);
  return {
    content: [{
      type: 'text',
      text: JSON.stringify({
        success: true,
        message: `已删除 ${args.entityNames.length} 个实体及其关联关系`
      }, null, 2)
    }]
  };
}

/**
 * 删除观察
 */
export async function deleteObservations(args: { deletions: { entityName: string; observations: string[] }[] }): Promise<any> {
  const manager = await getMemoryManager();
  await manager.deleteObservations(args.deletions);
  return {
    content: [{
      type: 'text',
      text: JSON.stringify({
        success: true,
        message: '观察已删除'
      }, null, 2)
    }]
  };
}

/**
 * 删除关系
 */
export async function deleteRelations(args: { relations: Relation[] }): Promise<any> {
  const manager = await getMemoryManager();
  await manager.deleteRelations(args.relations);
  return {
    content: [{
      type: 'text',
      text: JSON.stringify({
        success: true,
        message: `已删除 ${args.relations.length} 个关系`
      }, null, 2)
    }]
  };
}

/**
 * 读取整个知识图谱
 */
export async function readGraph(): Promise<any> {
  const manager = await getMemoryManager();
  const graph = await manager.readGraph();
  return {
    content: [{
      type: 'text',
      text: JSON.stringify({
        entities: graph.entities.length,
        relations: graph.relations.length,
        graph
      }, null, 2)
    }]
  };
}

/**
 * 搜索节点
 */
export async function searchNodes(args: { query: string }): Promise<any> {
  const manager = await getMemoryManager();
  const graph = await manager.searchNodes(args.query);
  return {
    content: [{
      type: 'text',
      text: JSON.stringify({
        query: args.query,
        found: {
          entities: graph.entities.length,
          relations: graph.relations.length
        },
        graph
      }, null, 2)
    }]
  };
}

/**
 * 按名称打开节点
 */
export async function openNodes(args: { names: string[] }): Promise<any> {
  const manager = await getMemoryManager();
  const graph = await manager.openNodes(args.names);
  return {
    content: [{
      type: 'text',
      text: JSON.stringify({
        requested: args.names,
        found: {
          entities: graph.entities.length,
          relations: graph.relations.length
        },
        graph
      }, null, 2)
    }]
  };
}
